'use client';

import { Menu } from '@base-ui/react/menu';
import clsx from 'clsx';
import { type CSSProperties, type ReactNode, type Ref, useRef } from 'react';

import '@repo/sds/components/SDSMenu/SDSMenu.scss';

export interface SDSMenuProps {
  children: ReactNode;
  trigger: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  // Positioning
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  alignOffset?: number;

  // Styling
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  style?: CSSProperties;
  contentStyle?: CSSProperties;

  // Behavior
  modal?: boolean;
  closeOnItemClick?: boolean;
  /** Mount the menu portal inside the trigger's nearest native dialog. */
  portalInClosestDialog?: boolean;

  dataTestId?: string;

  // Accessibility & Props for the trigger button
  triggerProps?: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    'data-testid'?: string;
  };
  ref?: Ref<HTMLDivElement>;
}

export function SDSMenu({
  children,
  trigger,
  open,
  onOpenChange,
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  alignOffset = 0,
  triggerClassName,
  contentClassName,
  style,
  contentStyle,
  modal = false,
  portalInClosestDialog = false,
  dataTestId,
  triggerProps,
  ref,
}: SDSMenuProps) {
  const contentClasses = clsx('SDSMenu', contentClassName);
  const triggerClasses = clsx('SDSMenu__trigger', triggerClassName);
  const dialogPortalContainerRef = useRef<HTMLElement | ShadowRoot | null>(null);

  const captureTrigger = (element: HTMLElement | null): void => {
    dialogPortalContainerRef.current = portalInClosestDialog ? (element?.closest('dialog') ?? null) : null;
  };

  return (
    <Menu.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <Menu.Trigger ref={captureTrigger} className={triggerClasses} style={style} {...triggerProps}>
        {trigger}
      </Menu.Trigger>

      <Menu.Portal container={portalInClosestDialog ? dialogPortalContainerRef : undefined}>
        <Menu.Positioner
          className="SDSMenu__positioner"
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
        >
          <Menu.Popup ref={ref} className={contentClasses} style={contentStyle} data-testid={dataTestId}>
            {children}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

export interface SDSMenuItemProps {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  className?: string;
  style?: CSSProperties;
  dataTestId?: string;
  ref?: Ref<HTMLDivElement>;
}

export function SDSMenuItem({
  children,
  onClick,
  disabled = false,
  destructive = false,
  className,
  style,
  dataTestId,
  ref,
}: SDSMenuItemProps) {
  const itemClasses = clsx(
    'SDSMenuItem',
    {
      'SDSMenuItem--disabled': disabled,
      'SDSMenuItem--destructive': destructive,
    },
    className,
  );

  return (
    <Menu.Item
      ref={ref}
      className={itemClasses}
      disabled={disabled}
      onClick={onClick}
      style={style}
      data-testid={dataTestId}
    >
      {children}
    </Menu.Item>
  );
}
